import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios';
import { useAuthStore } from '@/stores/authStore';
import { isWechatBrowser } from '@/utils/wechatJssdk';

/** Axios 实例 */
const apiClient = axios.create({
  baseURL: '/api',
  timeout: 15000,
});

/** 请求拦截器：注入 JWT + 租户头 + 自动处理 FormData 的 Content-Type */
apiClient.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const { token, currentTenant } = useAuthStore.getState();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    if (currentTenant?.id) {
      config.headers['X-Tenant-Id'] = currentTenant.id;
    }
    // 发送 FormData 时必须让浏览器自动设置 Content-Type: multipart/form-data; boundary=...
    // axios 1.x headers 通常是 AxiosHeaders 实例，但这里同时兼容普通对象写法，
    // 避免某些场景下 delete() 未生效导致后端 multer 解析不到文件而请求挂死。
    if (config.data instanceof FormData) {
      if (config.headers && typeof config.headers.delete === 'function') {
        config.headers.delete('Content-Type');
      }
      // 兜底：即使是普通对象也强制删除 key
      delete (config.headers as Record<string, any>)['Content-Type'];
    }
    return config;
  },
  (error) => Promise.reject(error)
);

/** 响应拦截器：统一错误处理 + 401 自动刷新 */
apiClient.interceptors.response.use(
  (response) => {
    const data = response.data;
    // 业务错误码非 0 时抛出
    if (data && data.code !== undefined && data.code !== 0) {
      return Promise.reject(new Error(data.message || '请求失败'));
    }
    return response;
  },
  async (error: AxiosError) => {
    const status = error.response?.status;
    const originalUrl = error.config?.url || '';
    // 登录/注册/验证等公开端点返回 401 应交给业务页面显示错误，不要直接跳转
    const isPublicAuthEndpoint =
      originalUrl === '/auth/login' ||
      originalUrl === '/api/auth/login' ||
      originalUrl === '/auth/register-initiate' ||
      originalUrl === '/api/auth/register-initiate';

    if (status === 401 && !isPublicAuthEndpoint) {
      // Token 过期，尝试刷新
      const { refreshToken, clearAuth, token: currentToken } = useAuthStore.getState();
      // 方案 A 配套：微信内静默登录可能在后台刚兑换出新 token，而「旧 token 的在途请求」401 属预期噪音
      // —— 此时 store 里的 token 比请求时用的新，直接用新 token 重放即可，绝不能走 clearAuth
      //（否则会把刚兑换到的新登录态一并清掉，用户被踢回 /login 弹注册页 —— 2026-10-07 实测根因）。
      const sentAuth = (error.config?.headers as Record<string, unknown> | undefined)?.Authorization;
      const sentToken = sentAuth ? String(sentAuth).replace(/^Bearer\s+/i, '') : null;
      if (sentToken && currentToken && currentToken !== sentToken && error.config) {
        error.config.headers.Authorization = `Bearer ${currentToken}`;
        return apiClient(error.config);
      }
      // 微信内 401 的最终兜底不跳 /login（等于逼用户注册），改走静默登录重新授权；
      // 非微信环境维持原行为。
      const backToWxEntry = () => {
        const { origin, pathname, search } = window.location;
        // BrowserRouter：完整路由就是 pathname+search（main.tsx 启动垫片已把 #/ 深链归位）。
        // 登录页/首页回落首页，其余路径原样带回（保住 act 等深链参数）。
        const back = (pathname === '/login' || pathname === '/') ? origin + '/' : origin + pathname + search;
        window.location.href = '/api/auth/wx/entry?back=' + encodeURIComponent(back);
      };
      if (refreshToken) {
        try {
          const res = await axios.post('/api/auth/refresh', { token: refreshToken });
          const newToken = res.data.data.token;
          useAuthStore.getState().setToken(newToken);
          // 重放原请求
          const originalRequest = error.config;
          if (originalRequest) {
            originalRequest.headers.Authorization = `Bearer ${newToken}`;
            return apiClient(originalRequest);
          }
        } catch {
          clearAuth();
          if (isWechatBrowser()) backToWxEntry();
          else window.location.href = '/login';
        }
      } else {
        clearAuth();
        if (isWechatBrowser()) backToWxEntry();
        else window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

export default apiClient;
